import apiService from "./api";

export type ChatRole = "USER" | "ASSISTANT";

export interface ChatMessage {
	id: string;
	role: ChatRole;
	content: string;
	createdAt: string;
	/** Cards de criação salvos com a mensagem (voltam no histórico). */
	actions?: PendingAction[];
	/**
	 * Opções que o assistente ofereceu como botões. Só valem enquanto a
	 * mensagem é a última da conversa (depois disso a pergunta já foi respondida).
	 */
	quickReplies?: string[];
}

export interface ChatHistoryResponse {
	conversationId: string | null;
	messages: ChatMessage[];
}

export interface PendingActionField {
	/** Campo do payload que este valor representa. */
	key?: string;
	label: string;
	value: string;
	/** Pode ser mudado no próprio card (botão Alterar). */
	editable?: boolean;
}

/** Proposta de criação vinda do assistente, para confirmação do usuário. */
export interface PendingAction {
	id: string;
	tool: string;
	resource: string; // chave do service do frontend
	title: string;
	payload: Record<string, any>;
	display: PendingActionField[];
	status?: "PENDING" | "EXECUTING" | "CONFIRMED" | "CANCELLED";
	/** Dependência que faltava: depois de criada, o chat segue sozinho com o cadastro pedido. */
	autoContinue?: boolean;
}

export interface SendMessageResponse {
	conversationId: string;
	reply: ChatMessage;
	pendingActions?: PendingAction[];
	/** Botões de resposta para a pergunta desta resposta. */
	quickReplies?: string[];
}

export interface SetupItem {
	key: string;
	count: number;
	done: boolean;
}

export interface SetupStatus {
	/** "essential" enquanto falta o básico (incl. 1 ritual); "optional" depois. */
	phase: "essential" | "optional";
	items: SetupItem[];
	done: number;
	total: number;
	essentialDone: boolean;
	optionalItems: SetupItem[];
	optionalDone: number;
	optionalTotal: number;
	allDone: boolean;
}

/** Grava a decisão do usuário sobre um card, para a recarga não reabri-lo. */
export const updateAssistantActionStatus = (
	actionId: string,
	status: NonNullable<PendingAction["status"]>,
	/** Campos alterados no card (Alterar), mandados junto da reserva. */
	payload?: Record<string, unknown>,
): Promise<{ status: NonNullable<PendingAction["status"]> }> =>
	apiService.patch(`/assistant/actions/${actionId}`, payload ? { status, payload } : { status });

/** Busca a conversa mais recente do usuário (histórico de até 24h). */
export const getChatHistory = (): Promise<ChatHistoryResponse> =>
	apiService.get<ChatHistoryResponse>("/assistant/history");

/** Progresso dos cadastros essenciais do terreiro (card do dashboard). */
export const getSetupStatus = (): Promise<SetupStatus> =>
	apiService.get<SetupStatus>("/assistant/setup-status");

/** Envia uma mensagem ao assistente e retorna a resposta gerada. */
export const sendChatMessage = (
	message: string,
	conversationId?: string | null,
): Promise<SendMessageResponse> =>
	apiService.post<SendMessageResponse>("/assistant/messages", {
		message,
		...(conversationId ? { conversationId } : {}),
	});

/** Limpa todo o histórico de conversa do usuário. */
export const clearChatHistory = (): Promise<void> =>
	apiService.delete<void>("/assistant/history");
